import axios from 'axios';
import { isNewApiFormat, unwrapApiResponse } from './unwrapApiResponse';

/**
 * Attaches the logged-in user's backend token to every request bound for an
 * mrc.ai backend, so individual fetch/axios call sites don't have to.
 *
 * - Only mrc.ai hosts get the token; third-party calls (Auth0, ...)
 *   are left alone.
 * - A caller that sets its own Authorization header keeps it.
 * - The login endpoints are skipped: they issue the token, and a stale one
 *   sent along could block re-login.
 * - The token is read from localStorage on every request, so it follows
 *   login/logout without any wiring to AuthContext.
 *
 * It also unwraps successful responses in the standard API envelope
 * ({ meta, response: { data }, error, isSuccess }) so call sites receive the
 * data itself, exactly as they did before each API adopted the envelope.
 * Error responses (non-2xx) are passed through untouched. Code that already
 * calls unwrapApiResponse keeps working: it returns non-envelope data as-is.
 */

const LOGIN_ENDPOINTS = [
  process.env.REACT_APP_AUTH_SESSION_ENDPOINT,
].filter(Boolean);

export const getStoredToken = () => {
  try {
    return JSON.parse(localStorage.getItem('user'))?.idToken || null;
  } catch {
    return null;
  }
};

const needsToken = (rawUrl) => {
  let url;
  try {
    url = new URL(rawUrl, window.location.href);
  } catch {
    return false;
  }
  const { hostname } = url;
  if (hostname !== 'mrc.ai' && !hostname.endsWith('.mrc.ai')) return false;
  const bare = url.origin + url.pathname;
  return !LOGIN_ENDPOINTS.some((endpoint) => bare === endpoint);
};

const isSuccessEnvelope = (body) => isNewApiFormat(body) && body.isSuccess === true;

/**
 * If a successful mrc.ai JSON response is in the envelope, returns a new
 * Response whose body is just the unwrapped data. Anything else is returned
 * unchanged.
 */
const unwrapFetchEnvelope = async (response) => {
  const contentType = response.headers.get('content-type') || '';
  if (!response.ok || !contentType.includes('application/json')) return response;

  let body;
  try {
    body = await response.clone().json();
  } catch {
    return response; // empty or not really JSON: leave it to the caller
  }
  if (!isSuccessEnvelope(body)) return response;

  const headers = new Headers(response.headers);
  headers.delete('content-length'); // the body is changing size
  return new Response(JSON.stringify(unwrapApiResponse(body) ?? null), {
    status: response.status,
    statusText: response.statusText,
    headers,
  });
};

const installFetch = () => {
  const originalFetch = window.fetch.bind(window);

  const withToken = (input, init) => {
    const token = getStoredToken();
    if (!token) return originalFetch(input, init);

    const headers = new Headers(
      init.headers || (input instanceof Request ? input.headers : undefined)
    );
    if (headers.has('Authorization')) return originalFetch(input, init);
    headers.set('Authorization', `Bearer ${token}`);

    return originalFetch(input, { ...init, headers });
  };

  window.fetch = (input, init = {}) => {
    const url = input instanceof Request ? input.url : String(input);
    if (!needsToken(url)) return originalFetch(input, init);

    return withToken(input, init).then(unwrapFetchEnvelope);
  };
};

const installAxios = () => {
  axios.interceptors.request.use((config) => {
    const token = getStoredToken();
    const url = axios.getUri(config);
    if (token && needsToken(url) && !config.headers.has('Authorization')) {
      config.headers.set('Authorization', `Bearer ${token}`);
    }
    return config;
  });

  // Unwrap successful envelope responses for mrc.ai hosts. Errors (non-2xx)
  // still reject as before, with the envelope available on error.response.data.
  axios.interceptors.response.use((response) => {
    if (needsToken(axios.getUri(response.config)) && isSuccessEnvelope(response.data)) {
      return { ...response, data: unwrapApiResponse(response.data), _originalResponse: response.data };
    }
    return response;
  });
};

let installed = false;

export const installAuthToken = () => {
  if (installed) return;
  installed = true;
  installFetch();
  installAxios();
};
